import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';
import { usePageMeta } from '../hooks/usePageMeta';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import ScrollToTop from '../components/ScrollToTop';
import Reveal from '../components/Reveal';
import Hero from '../components/Hero';
import * as FiIcons from 'react-icons/fi';

const { FiCheck, FiArrowRight, FiCode, FiBox, FiPenTool, FiFilm } = FiIcons;

const Services = () => {
  const { currentLanguage } = useLanguage();
  const t = translations[currentLanguage];
  usePageMeta({ title: t.services.title, description: t.hero.description, path: 'services' });

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <Hero gradientClass="hero-gradient-services" title={t.nav.services} />

      {/* Services Offered Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="mb-16 text-center">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-stone-900">
              {t.services.title}
            </h2>
          </Reveal>

          {/* Service 1: Websites (Navy BG, White Text) */}
          <Reveal whileHover={{ y: -4 }} className="mb-8 p-8 md:p-12 bg-gradient-to-br from-navy-900 to-brand-dark text-white rounded-sm relative">
            <div className="absolute top-8 right-8">
              <SafeIcon icon={FiCode} className="h-12 w-12 text-amber-300 opacity-80" />
            </div>
            <h3 className="font-serif text-2xl md:text-3xl font-bold mb-4 text-white">
              {t.services.websites.title}
            </h3>
            <p className="text-lg mb-6 text-stone-300">
              {t.services.websites.description}
            </p>
            <ul className="space-y-3 mb-6">
              {t.services.websites.items.map((item, index) => (
                <li key={index} className="flex items-start text-stone-300">
                  <span className="mr-3 text-amber-400 font-bold">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-stone-300">
              {t.services.websites.tech}
            </p>
          </Reveal>

          {/* Service 2: Contao (Light BG, Dark Text) */}
          <Reveal whileHover={{ y: -4 }} className="mb-8 p-8 md:p-12 bg-stone-50 text-stone-900 rounded-sm border border-stone-200 relative">
            <div className="absolute top-8 right-8">
              <SafeIcon icon={FiBox} className="h-12 w-12 text-amber-700 opacity-70" />
            </div>
            <h3 className="font-serif text-2xl md:text-3xl font-bold mb-4">
              {t.services.contao.title}
            </h3>
            <p className="text-lg text-stone-700 mb-6">
              {t.services.contao.description}
            </p>
            <ul className="space-y-3 mb-6">
              {t.services.contao.items.map((item, index) => (
                <li key={index} className="flex items-start text-stone-700">
                  <span className="mr-3 text-amber-700 font-bold">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-stone-700">
              {t.services.contao.tech}
            </p>
          </Reveal>

          {/* Service 3: Content (Navy BG, White Text) */}
          <Reveal whileHover={{ y: -4 }} className="mb-8 p-8 md:p-12 bg-gradient-to-br from-navy-900 to-brand-dark text-white rounded-sm relative">
            <div className="absolute top-8 right-8">
              <SafeIcon icon={FiPenTool} className="h-12 w-12 text-amber-300 opacity-80" />
            </div>
            <h3 className="font-serif text-2xl md:text-3xl font-bold mb-4 text-white">
              {t.services.content.title}
            </h3>
            <p className="text-lg mb-6 text-white">
              <Link to="/about" className="underline hover:text-amber-200 transition-colors">
                {t.services.content.introLinkText}
              </Link>
              {t.services.content.intro.substring(t.services.content.introLinkText.length)}
            </p>
            <ul className="space-y-3 mb-6">
              {t.services.content.items.map((item, index) => (
                <li key={index} className="flex items-start text-white">
                  <span className="mr-3 text-amber-400 font-bold">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-white">
              {t.services.content.note}
            </p>
          </Reveal>

          {/* Service 4: Documentaries (Light BG, Dark Text) */}
          <Reveal whileHover={{ y: -4 }} className="p-8 md:p-12 bg-stone-50 text-stone-900 rounded-sm border border-stone-200 relative">
            <div className="absolute top-8 right-8">
              <SafeIcon icon={FiFilm} className="h-12 w-12 text-amber-700 opacity-70" />
            </div>
            <h3 className="font-serif text-2xl md:text-3xl font-bold mb-4">
              {t.services.documentaries.title}
            </h3>
            <p className="text-lg text-stone-700 mb-6">
              {t.services.documentaries.description}
            </p>
            <ul className="space-y-3 mb-6">
              {t.services.documentaries.items.map((item, index) => (
                <li key={index} className="flex items-start text-stone-700">
                  <span className="mr-3 text-amber-700 font-bold">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-stone-700">
              {t.services.documentaries.result}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-16">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-stone-900 mb-6">
              {t.process.title}
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { num: '01', title: t.process.step1.title, desc: t.process.step1.description },
              { num: '02', title: t.process.step2.title, desc: t.process.step2.description },
              { num: '03', title: t.process.step3.title, desc: t.process.step3.description },
              { num: '04', title: t.process.step4.title, desc: t.process.step4.description }
            ].map((step, index) => (
              <Reveal
                key={step.num}
                delay={index * 0.12}
                whileHover={{ y: -6 }}
                className="relative p-6 bg-stone-100 rounded-sm"
              >
                <motion.div
                  initial={{ opacity: 0.3 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ delay: index * 0.12 + 0.2, duration: 0.6 }}
                  viewport={{ once: true }}
                  className="font-serif text-5xl font-bold text-amber-200 absolute top-4 right-4"
                >
                  {step.num}
                </motion.div>
                <h3 className="text-xl font-bold text-stone-900 mb-4 relative z-10">{step.title}</h3>
                <p className="text-stone-500 relative z-10">{step.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Included Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="mb-16 text-center">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-stone-900">
              {t.included.title}
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {t.included.items && t.included.items.map((item, index) => (
              <Reveal
                key={index}
                delay={(index % 2) * 0.1}
                whileHover={{ y: -5 }}
                className="p-6 bg-stone-100 rounded-sm border border-stone-200 hover:shadow-lg transition-shadow"
              >
                <h3 className="text-lg font-bold text-stone-900 mb-3 flex items-start">
                  <span className="text-amber-700 mr-3 font-bold">✓</span>
                  {item.title}
                </h3>
                <p className="text-stone-700 text-sm leading-relaxed">
                  {item.description}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Example Section */}
      <section className="py-20 bg-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="bg-white rounded-sm shadow-xl overflow-hidden max-w-4xl mx-auto border border-stone-200 p-8 md:p-12">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-stone-900 mb-2">
              {t.example.title}
            </h2>
            <p className="text-lg text-stone-700 mb-8 font-semibold">
              {t.example.subtitle}
            </p>

            <div className="mb-8">
              <h3 className="text-xs font-bold text-amber-700 mb-3 uppercase tracking-eyebrow">Features</h3>
              <ul className="space-y-2">
                {t.example.features && t.example.features.map((feature, index) => (
                  <li key={index} className="flex items-start text-stone-700">
                    <span className="mr-3 text-amber-700 font-bold">•</span>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="border-t border-stone-200 pt-6 space-y-4 mb-8">
              <div>
                <p className="text-sm text-stone-500 mb-2">
                  {t.example.servicePackage}
                </p>
                <p className="font-serif text-2xl font-bold text-amber-700">
                  {t.example.priceOneYear}
                </p>
              </div>

              <div className="bg-stone-50 p-4 rounded-sm">
                <p className="text-sm text-stone-500 mb-2">
                  {t.example.servicePackageThreeYears}
                </p>
                <p className="font-serif text-2xl font-bold text-amber-700">
                  {t.example.priceThreeYears}
                </p>
              </div>
            </div>

            <div className="text-center">
              <Link
                to="/request"
                className="inline-flex items-center px-8 py-4 bg-amber-700 text-white font-semibold rounded-sm hover:bg-amber-800 transition-colors duration-200 shadow-md"
              >
                {t.hero.cta}
                <SafeIcon icon={FiArrowRight} className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
      <ScrollToTop />
    </div>
  );
};

export default Services;
